import React, { useCallback, useEffect, useState } from "react";
import { useMap } from "@vis.gl/react-google-maps";
import { MarkerClusterer } from "@googlemaps/markerclusterer";
import MovingMarkerWithInfoWindow from "./MovingMarkerWithInfoWindow";

class CustomClusterRenderer {
  constructor() {
    this.markers = new Map();
  }

  /**
   * Render method to create the cluster marker.
   * @param {google.maps.LatLng} position - The cluster's position.
   * @param {number} count - The number of markers in the cluster.
   * @param {Array} markers - The markers within this cluster.
   * @return {google.maps.Marker} The cluster marker.
   */
  render({ position, count, markers }) {
    const meanClusterSize =
      markers.reduce((acc, marker) => {
        const label = marker.getLabel ? marker.getLabel() : { text: "" };
        return acc + (label && label.text ? parseInt(label.text, 10) : 0);
      }, 0) / markers.length || 0;

    const color =
      count > Math.max(10, meanClusterSize)
        ? "#ff0000"
        : count > 5
          ? "#0000ff"
          : "#118011";

    const svg = window.btoa(`
      <svg fill="${color}" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 240 240">
        <circle cx="120" cy="120" opacity=".6" r="70" />
        <circle cx="120" cy="120" opacity=".3" r="90" />
        <circle cx="120" cy="120" opacity=".2" r="110" />
        <circle cx="120" cy="120" opacity=".1" r="130" />
      </svg>
    `);

    const marker = new window.google.maps.Marker({
      position,
      icon: {
        url: `data:image/svg+xml;base64,${svg}`,
        scaledSize: new window.google.maps.Size(45, 45),
      },
      label: {
        text: String(count),
        color: "rgba(255,255,255,0.9)",
        fontSize: "12px",
      },
      zIndex: window.google.maps.Marker.MAX_ZINDEX + count,
    });

    this.markers.set(marker, { position, count });

    return marker;
  }
}

const MarkerClusterGM = ({
  vehicles,
  jobVehicleInfoToggle,
  handleAdvancedDirectionResponse,
  advancedDirectionVehicle,
}) => {
  const [markers, setMarkers] = useState({});

  const map = useMap();

  useEffect(() => {
    if (!map || Object.keys(markers).length === 0) return;

    const clusterer = new MarkerClusterer({
      map,
      renderer: new CustomClusterRenderer(),
    });

    clusterer.addMarkers(Object.values(markers));

    return () => {
      clusterer.clearMarkers();
      clusterer.setMap(null);
    };
  }, [map, markers]);

  const setMarkerRef = useCallback((marker, key) => {
    setMarkers((markers) => {
      if (!marker) {
        const { [key]: removed, ...remainingMarkers } = markers;
        return remainingMarkers;
      } else {
        return { ...markers, [key]: marker };
      }
    });
  }, []);

  return (
    <>
      {vehicles.map((vehicle) => (
        <MovingMarkerWithInfoWindow
          vehicle={vehicle}
          key={vehicle.terminalId}
          setMarkerRef={setMarkerRef}
          jobVehicleInfoToggle={jobVehicleInfoToggle}
          handleAdvancedDirectionResponse={handleAdvancedDirectionResponse}
          advancedDirectionVehicle={advancedDirectionVehicle}
        />
      ))}
    </>
  );
};

export default MarkerClusterGM;
